import { useState } from "react";
import type { CreateImportRequest, ImportModel } from "@recipevault/shared";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import { Label } from "@/ui/label.js";
import { Textarea } from "@/ui/textarea.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/tabs.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select.js";

export interface ImportFormProps {
  onSubmit: (req: CreateImportRequest) => void;
  submitting?: boolean;
  defaultModel?: ImportModel;
  defaultKind?: "url" | "text";
  /** Seeds the "Paste text" textarea — e.g. from a Web Share Target hand-off. */
  defaultText?: string | undefined;
  /**
   * Fired on every change to the URL input (url-mode only). Lets a parent run
   * duplicate-import detection without lifting the input's state entirely.
   */
  onUrlChange?: (url: string) => void;
  /**
   * Fired whenever the URL/Paste-text tab changes. Lets a parent clear
   * tab-scoped state (e.g. a URL-mode duplicate-import warning) when the
   * user switches away from that tab.
   */
  onKindChange?: (kind: "url" | "text") => void;
  /**
   * Overrides the submit button's idle label — e.g. "Import anyway" once the
   * parent has detected the pasted URL already exists in the library. Ignored
   * while `submitting` (still shows "Submitting…").
   */
  submitLabel?: string;
}

export function ImportForm({
  onSubmit,
  submitting,
  defaultModel = "haiku",
  defaultKind = "url",
  defaultText,
  onUrlChange,
  onKindChange,
  submitLabel = "Import recipe",
}: ImportFormProps): React.ReactElement {
  const [kind, setKind] = useState<"url" | "text">(defaultKind);
  const [url, setUrl] = useState("");
  const [text, setText] = useState(defaultText ?? "");
  const [model, setModel] = useState<ImportModel>(defaultModel);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    if (kind === "url") {
      onSubmit({ kind: "url", url, model });
    } else {
      onSubmit({ kind: "text", text, model });
    }
  }

  const disabled =
    submitting === true ||
    (kind === "url" ? url.trim().length === 0 : text.trim().length < 20);

  return (
    <form onSubmit={submit} className="space-y-4">
      <Tabs
        value={kind}
        onValueChange={(v) => {
          const next = v as "url" | "text";
          setKind(next);
          onKindChange?.(next);
        }}
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="url">From URL</TabsTrigger>
          <TabsTrigger value="text">Paste text</TabsTrigger>
        </TabsList>
        <TabsContent value="url" className="space-y-2 pt-3">
          <Label htmlFor="recipe-url">Recipe URL</Label>
          <Input
            id="recipe-url"
            type="url"
            placeholder="https://..."
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              onUrlChange?.(e.target.value);
            }}
            inputMode="url"
            autoComplete="url"
          />
        </TabsContent>
        <TabsContent value="text" className="space-y-2 pt-3">
          <Label htmlFor="recipe-text">Recipe text</Label>
          <Textarea
            id="recipe-text"
            rows={10}
            placeholder="Paste ingredients and steps…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={50_000}
          />
          <p className="text-xs text-muted-foreground">
            {text.length} / 50000 chars
          </p>
        </TabsContent>
      </Tabs>

      <div>
        <button
          type="button"
          className="text-sm text-muted-foreground underline-offset-2 hover:underline"
          onClick={() => setAdvancedOpen((s) => !s)}
          aria-expanded={advancedOpen}
        >
          {advancedOpen ? "Hide advanced" : "Advanced…"}
        </button>
        {advancedOpen && (
          <div className="mt-2 space-y-2 rounded-md border border-border p-3">
            <Label htmlFor="model-select">Model</Label>
            <Select
              value={model}
              onValueChange={(v) => setModel(v as ImportModel)}
            >
              <SelectTrigger id="model-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="haiku">Haiku (default; cheapest)</SelectItem>
                <SelectItem value="sonnet">Sonnet (stronger; ~6× cost)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Button type="submit" disabled={disabled} className="w-full">
        {submitting === true ? "Submitting…" : submitLabel}
      </Button>
    </form>
  );
}
